import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import type { HealthResponse } from '@siteshowroom/shared'
import { StatusBadge, toBadgeStatus } from '../components/status-badge'
import { fetchHealth } from '../lib/api-client'
import type { ApiFailure } from '../lib/api-client'
import { useLocale, useTranslate } from '../lib/i18n-provider'

/**
 * Systeempagina: het bewijs dat de SPA de API daadwerkelijk bereikt.
 *
 * Toont per gezondheidscontrole de status en de latency, plus het aantal
 * toegepaste migraties en de actieve storagedriver. Dit is de pagina waarmee je
 * in stage 0 vaststelt dat database, migraties en opslag echt bereikbaar zijn.
 */

type PageState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly health: HealthResponse; readonly roundTripMs: number }
  | { readonly phase: 'error'; readonly error: ApiFailure }

export function SystemPage(): ReactElement {
  const t = useTranslate()
  const { locale } = useLocale()
  const [state, setState] = useState<PageState>({ phase: 'loading' })
  const controllerRef = useRef<AbortController | null>(null)

  // Getallen en eenheden altijd via Intl formatteren, nooit met de hand (§17.8).
  const numberFormat = useMemo(() => new Intl.NumberFormat(locale), [locale])
  const durationFormat = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'millisecond',
        unitDisplay: 'short',
        maximumFractionDigits: 0,
      }),
    [locale],
  )

  const load = useCallback(async (): Promise<void> => {
    // Een lopende aanvraag afbreken voorkomt dat een traag antwoord een nieuwer
    // antwoord overschrijft.
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller

    // Bewust géén setState vóór de await: de begintoestand is al 'loading', en
    // synchroon state zetten vanuit een effect veroorzaakt een cascade van renders.
    // De herlaadknop zet 'loading' zelf, want daar is het wél een reactie op invoer.
    const result = await fetchHealth({ signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    if (result.ok) {
      setState({ phase: 'ready', health: result.data, roundTripMs: result.roundTripMs })
    } else {
      setState({ phase: 'error', error: result.error })
    }
  }, [])

  useEffect(() => {
    // De regel `set-state-in-effect` ziet dat `load` uiteindelijk setState aanroept
    // en waarschuwt voor cascaderende renders. Hier is dat niet aan de orde: `load`
    // zet pas state ná de await, de begintoestand is al 'loading', en de cleanup
    // breekt de aanvraag af. Dit is precies het ophaalpatroon dat de React-documentatie
    // voor een effect beschrijft; de regel kan het onderscheid statisch niet maken.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
    return () => {
      controllerRef.current?.abort()
    }
  }, [load])

  return (
    <section className="page" aria-labelledby="system-heading">
      <div className="page__header">
        <h1 className="page__title" id="system-heading">
          {t('dashboard.system.title')}
        </h1>
        <button
          className="button"
          type="button"
          onClick={() => {
            setState({ phase: 'loading' })
            void load()
          }}
          disabled={state.phase === 'loading'}
        >
          {t('common.action.reload')}
        </button>
      </div>

      {state.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}

      {state.phase === 'error' && (
        <div className="notice notice--error" role="alert">
          <p className="notice__title">{t('common.state.error')}</p>
          {/* Technisch detail, bewust onvertaald: het is diagnose-informatie. */}
          <code className="notice__detail">{state.error.message}</code>
        </div>
      )}

      {state.phase === 'ready' && (
        <>
          <div className="page__summary">
            <StatusBadge
              status={toBadgeStatus(state.health.status)}
              label={t(`dashboard.system.status.${toBadgeStatus(state.health.status)}`)}
            />
          </div>

          <table className="table">
            <caption className="table__caption">{t('dashboard.system.checks')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('dashboard.system.check')}</th>
                <th scope="col">{t('dashboard.system.checkStatus')}</th>
                <th scope="col">{t('dashboard.system.latency')}</th>
              </tr>
            </thead>
            <tbody>
              {/* `checks` is een object met vaste sleutels, geen lijst: het gedeelde
                  HealthResponse-contract benoemt iedere controle expliciet, zodat de
                  API er geen naar believen bij kan verzinnen. */}
              {Object.entries(state.health.checks).map(([name, check]) => (
                <tr key={name}>
                  <th scope="row">{t(`dashboard.health.${name}`)}</th>
                  <td>
                    <StatusBadge
                      status={toBadgeStatus(check.status)}
                      label={t(`dashboard.system.status.${toBadgeStatus(check.status)}`)}
                    />
                  </td>
                  <td className="table__number">
                    {check.latencyMs === null
                      ? t('dashboard.system.notMeasured')
                      : durationFormat.format(check.latencyMs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <dl className="facts">
            <div className="facts__item">
              <dt>{t('dashboard.system.migrations')}</dt>
              <dd>
                {state.health.checks.database.appliedMigrations === null
                  ? t('dashboard.system.notMeasured')
                  : numberFormat.format(state.health.checks.database.appliedMigrations)}
              </dd>
            </div>
            <div className="facts__item">
              <dt>{t('dashboard.system.storageDriver')}</dt>
              <dd>
                <code>{state.health.checks.storage.driver}</code>
              </dd>
            </div>
            <div className="facts__item">
              <dt>{t('dashboard.system.mailDriver')}</dt>
              <dd>
                <code>{state.health.checks.mail.driver}</code>
              </dd>
            </div>
            <div className="facts__item">
              <dt>{t('dashboard.system.roundTrip')}</dt>
              <dd>{durationFormat.format(state.roundTripMs)}</dd>
            </div>
          </dl>
        </>
      )}
    </section>
  )
}
